import { Box, Text, ToastType } from '@bechtel/gds';
import { OperatorType, UiTextEnum } from '@bechtel/sr-types';
import { setNestedObjectValues, useFormikContext } from 'formik';
import { defaultTo, find, isEmpty } from 'lodash';
import { useEffect } from 'react';
import {
  CommodityFooter,
  DateFieldComponent,
  FormField,
  FormLabelSize,
  FormSectionHeading,
  SelectFieldComponent,
  TextFieldComponent,
} from '~/components';
import { useDebounce, usePermissions, useSelectListQuery, useToast } from '~/hooks';
import { FeedbackCardStyled, FeedbackMainContainerStyled, FormContainerStyled } from '../styled';
import { FeedbackRacewayFormikState } from './types';

const SelectedItemComponent = (items) => {
  return <Text>{items.item.column2}</Text>;
};

const ItemComponent = (items) => {
  return <Text>{items.item.column2}</Text>;
};

const FeedbackRacewayForm = ({ searchRaceways, loading }) => {
  const { showToast } = useToast();
  const {
    setFieldValue,
    values,
    handleSubmit,
    handleReset,
    setFieldTouched,
    dirty,
    touched,
    isSubmitting,
    initialValues,
    setTouched,
    validateForm,
  } = useFormikContext<FeedbackRacewayFormikState>();
  const loadingCheck = loading || isEmpty(values.tag);

  const [getSelectListData, { data }] = useSelectListQuery('RACEWAY.CONSTRUCTION_STATUS');
  useEffect(() => {
    getSelectListData();
  }, [getSelectListData]);

  const { canUpdateRacewayFeedback } = usePermissions();
  const debouncedTag = useDebounce(values.tag, 400);
  useEffect(() => {
    if (debouncedTag) {
      searchRaceways({
        variables: {
          searchInput: {
            filterInput: {
              and: {
                field: 'tag',
                operator: OperatorType.EQUAL,
                value: debouncedTag,
              },
            },
          },
        },
        onCompleted: (data) => {
          if (data?.searchRaceways?.items?.length) {
            setFieldValue('tagInvalid', false);
          } else {
            setFieldValue('tagInvalid', true);
          }
        },
      });
    }
  }, [debouncedTag, searchRaceways, setFieldValue, values.tag, initialValues, setFieldTouched]);

  const defaultSelectedItem = defaultTo(
    find(data?.getSelectListData?.items, { column1: values.constructionStatus }),
    values.constructionStatus
  );

  const handleSave = async () => {
    const formErrors = await validateForm();
    if (formErrors) {
      if (Object.keys(formErrors).length > 0) {
        setTouched(setNestedObjectValues(formErrors, true));
        showToast(UiTextEnum.ONE_OR_MORE_FIELDS_ARE_INVALID, [''], ToastType.WARNING);
        return;
      }
    }
    handleSubmit();
  };
  return (
    <>
      <FeedbackMainContainerStyled>
        <FeedbackCardStyled>
          <FormContainerStyled>
            <Box mb={{ sm: '700' }}>
              <FormField
                isMainCommodity={true}
                memberName="tag"
                pickListName="RACEWAY_FEEDBACK"
                commodityName={`commodities/raceway`}
                displayMemberId="id"
                hasFieldPicklist={true}
              >
                <TextFieldComponent />
              </FormField>
            </Box>
            <FormSectionHeading heading="Status" />
            <Box mb={{ sm: '700' }}>
              <FormField
                memberName="constructionStatus"
                alwaysEditable={!loadingCheck && canUpdateRacewayFeedback}
                showPicker={false}
                readOnly={loadingCheck}
                validate={false}
              >
                <SelectFieldComponent
                  items={data?.getSelectListData?.items}
                  selectedComponent={SelectedItemComponent}
                  ItemComponent={ItemComponent}
                  onSelectedItemChange={(changes) => {
                    setFieldValue('constructionStatus', changes.selectedItem.column1);
                  }}
                  selectedItem={defaultSelectedItem}
                />
              </FormField>
            </Box>
            <FormSectionHeading heading="Installation" />
            <Box flexDirection={{ sm: 'column' }} mb={{ sm: '700' }}>
              <Box justifyContent={{ sm: 'space-between' }} mb={{ sm: '450' }}>
                <Box flexBasis={{ sm: '45%' }}>
                  <FormField
                    alwaysEditable={!loadingCheck && canUpdateRacewayFeedback}
                    memberName="actualLength"
                    required={true}
                  >
                    <TextFieldComponent />
                  </FormField>
                </Box>
                <Box flexBasis={{ sm: '45%' }}>
                  <FormField
                    alwaysEditable={!loadingCheck && canUpdateRacewayFeedback}
                    memberName="numberOfFittings"
                    size={FormLabelSize.MEDIUM}
                  >
                    <TextFieldComponent />
                  </FormField>
                </Box>
              </Box>
              <Box mb={{ sm: '450' }} justifyContent={{ sm: 'space-between' }}>
                <Box flexBasis={{ sm: '45%' }}>
                  <FormField
                    alwaysEditable={!loadingCheck && canUpdateRacewayFeedback}
                    memberName="installedNoOfSupports"
                  >
                    <TextFieldComponent />
                  </FormField>
                </Box>
                <Box flexBasis={{ sm: '45%' }}>
                  <FormField alwaysEditable={!loadingCheck && canUpdateRacewayFeedback} memberName="installedTime">
                    <DateFieldComponent
                      selected={values.installedTime && new Date(values.installedTime)}
                      onChange={(date) => {
                        setFieldValue('installedTime', date);
                        setFieldTouched('installedTime', true, false);
                      }}
                      showPopperArrow={false}
                      icon="calendar"
                      iconStyle="regular"
                    />
                  </FormField>
                </Box>
              </Box>
              <Box justifyContent={{ sm: 'space-between' }}>
                <Box flexBasis={{ sm: '45%' }}>
                  <FormField alwaysEditable={!loadingCheck && canUpdateRacewayFeedback} memberName="installedBy">
                    <TextFieldComponent />
                  </FormField>
                </Box>
              </Box>
            </Box>

            <FormSectionHeading heading="Inspection" />
            <Box css={{ gap: '72px' }} mb={{ sm: '700' }}>
              <Box css={{ width: '50%' }}>
                <FormField alwaysEditable={!loadingCheck && canUpdateRacewayFeedback} memberName="inspectedBy">
                  <TextFieldComponent />
                </FormField>
              </Box>
              <Box css={{ width: '50%' }}>
                <FormField alwaysEditable={!loadingCheck && canUpdateRacewayFeedback} memberName="inspectedTime">
                  <DateFieldComponent
                    selected={values.inspectedTime && new Date(values.inspectedTime)}
                    onChange={(date) => {
                      setFieldValue('inspectedTime', date);
                      setFieldTouched('inspectedTime', true, false);
                    }}
                    showPopperArrow={false}
                    icon="calendar"
                    iconStyle="regular"
                  />
                </FormField>
              </Box>
            </Box>
          </FormContainerStyled>
        </FeedbackCardStyled>
      </FeedbackMainContainerStyled>
      <CommodityFooter
        saveHandler={handleSave}
        loading={isSubmitting}
        disableSave={!dirty || !touched || !values.tag || !canUpdateRacewayFeedback}
        disableCancel={!dirty || !touched}
        cancelHandler={() => handleReset()}
      />
    </>
  );
};

export default FeedbackRacewayForm;
